import './assets/css/main.css';
import './assets/css/custom.scss';
import 'react-range-slider-input/dist/style.css';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import AdminLayout from './components/adminLayout';
import { SignIn } from './pages/signin';
import { useContext, useEffect, useState, useRef } from 'react';
import { onMessageListener, requestForToken } from './firebase';
import { updateFCM } from './api/context/auth';
import PushNotification from './components/common/PushNotification';
import { SelectionContext } from './Context/Store';
import newNotificationSound from './assets/images/new_notification.wav';
import notificationSound from './assets/images/notification.wav';
import TermsandConditions from './pages/TermsandConditions/TermsandConditions';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy/CookiePolicy';
import AudioUnlocker from './components/common/AudioUnlocker';

function App() {
  const [notification, setNotification] = useState(null);
  const [initials, setInitials] = useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);


  const { updateCount, setUpdateCount, notifications, setNotifications,
    notificationsAll, setNotificationsAll, messages, setMessages,
    messageUpdate, setMessageUpdate, ordersTrigger, setOrdersTrigger } = useContext(SelectionContext);

  const processedNotifications = useRef(new Set());

  // Handle notification function (centralized)
  const handleNewNotification = (payload, source = 'foreground') => {

    console.log('new notification arrived here');
    const { title, body } = payload.notification || {};
    const typeId = payload?.data?.orderId;
    const type = payload?.data?.type;
    const link = payload?.data?.click_action;
    const id = payload?.data?.id || payload?.id || Date.now().toString();


    // Prevent duplicate processing
    if (processedNotifications.current.has(id)) {
      console.log('Skipping duplicate notification:', id);
      return;
    }
    processedNotifications.current.add(id);

    const newNotification = {
      title,
      body,
      isRead: false,
      typeId,
      type,
      link,
      id,
      source
    };

    console.log(`Received ${source} message:`, newNotification);

    // Update state
    setNotification(newNotification);
    setNotifications((prev) => {
      const existingIndex = prev.findIndex((notif) => notif.id === id);
      if (existingIndex === -1) {
        const updated = [newNotification, ...prev];
        setNotificationsAll(updated);
        return updated;
      }
      return prev;
    });

    setUpdateCount((prev) => prev + 1);
    setMessages((prev) => [newNotification, ...prev]);
    setMessageUpdate(true);
    setOrdersTrigger(true);


    console.log('New notification received:', process.env.NODE_ENV);

    // Play Notification Sound
    const audio = new Audio(process.env.NODE_ENV === "production" ? notificationSound : newNotificationSound);
    audio.play().catch((err) => console.error("Failed to play sound:", err));


  };

  // Service Worker Registration and Message Handling
  useEffect(() => {
    console.log('App component mounted');

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/firebase-messaging-sw.js")
        .then((registration) => {
          console.log("Service Worker registered with scope:", registration.scope);
          setServiceWorkerReady(true);

          // Tell the SW that React is ready
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'APP_READY' });
          }

          // Listen for messages from service worker (background notifications)
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type_message === 'NEW_NOTIFICATION') {
              console.log('Received message from service worker:', event.data);
              handleNewNotification({
                notification: {
                  title: event.data.title,
                  body: event.data.body
                },
                data: {
                  orderId: event.data.typeId,
                  type: event.data.type,
                  click_action: event.data.link,
                  id: event.data.id
                }
              }, 'background');
            }
          });
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  // Firebase Foreground Message Listener (only when service worker is NOT ready)
  useEffect(() => {
    if (!serviceWorkerReady && typeof Notification !== "undefined") {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          onMessageListener().then((payload) => {
            if (payload) {
              handleNewNotification(payload, 'foreground');
            }
          }).catch(error => {
            console.error('Foreground message listener error:', error);
          });
        } else {
          console.error('Unable to get permission to notify.');
        }
      });
    }
  }, [serviceWorkerReady]);

  // Token Management
  let notificationToken = null;

  const onResponse = (error, token) => {
    if (error) {
      console.log(error);
      toast.error(error);
    } else {
      notificationToken = token;
      console.log("FCM Token generated successfully:", token);

      updateFCM({ fcmToken: token })((response) => {
        if (response?.status) {
          console.log('FCM token updated successfully');
        }
      });
    }
  };

  const handleRequestForToken = async () => {
    const token = await requestForToken();
    if (token) {
      onResponse(null, token);
    }
  };

  useEffect(() => {
    if (initials) {
      const interval = setInterval(() => {
        handleRequestForToken();
      }, 5 * 60 * 1000);

      return () => clearInterval(interval);
    } else {
      setInitials(true);
      handleRequestForToken();
    }
  }, [initials]);

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Clean up processed notifications periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // Keep only notifications from the last hour to prevent memory leaks
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      processedNotifications.current = new Set(
        Array.from(processedNotifications.current).filter(id => {
          const timestamp = parseInt(id);
          return !isNaN(timestamp) && timestamp > oneHourAgo;
        })
      );
    }, 30 * 60 * 1000); // Clean every 30 minutes

    return () => clearInterval(cleanupInterval);
  }, []);

  return (
    <div>
      <Toaster />
      <BrowserRouter>
        <Routes>
          <Route path='/*' element={<AdminLayout />} />
          <Route exact path="/login" element={<SignIn />} />
          <Route exact path="/terms-and-conditions" element={<TermsandConditions />} />
          <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route exact path="/cookie-policy" element={<CookiePolicy />} />
        </Routes>
        {notification && (
          <PushNotification
            title={notification?.title}
            body={notification?.body}
            onClose={handleCloseNotification}
            notification={notification}
          />
        )}
        <AudioUnlocker onUnlock={setAudioEnabled} />
      </BrowserRouter>

    </div>
  );
}

export default App;